import * as React from 'react';
import type { KeyboardEvent, MouseEvent, MouseEventHandler } from 'react';
import { cx } from '../utils';
import { devWarn, IS_PROD } from '../internal/devWarn';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeProps } from '../escape-hatches';

/* `interactive` card activation event — the same union shape as
   TableRow's. Accepts both the real mouse event (pointer path)
   and the real keyboard event (Enter / Space path). Do not
   synthesise a MouseEvent from a KeyboardEvent; consumers reading
   e.button / e.clientX / e.altKey need the actual origin. */
export type CardActivationEvent = MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>;
export type CardActivationHandler = (e: CardActivationEvent) => void;

/* Allowed rest-prop pass-through: `id`, `title`, `lang`, `dir`,
   `data-*`, `aria-*`. Per the codex Divider audit (rounds 2/3) and
   Card audit (round 1), the type-level guardrail isn't enough — a
   typed-bag spread (`<Card {...(bag as any)}>` or via plain JS) can
   still smuggle `role`, `tabIndex`, `dangerouslySetInnerHTML`, or
   other reserved keys at runtime. `pickAllowed` filters the spread
   on the way out so the controlled a11y contract holds regardless of
   the caller's discipline.

   Type shape borrows from `React.HTMLAttributes<HTMLElement>` so the
   `id`/`title`/`lang`/`dir` slots use React's exact attribute types
   (string-typed, not `unknown`). Codex adversarial review 2026-05-18
   r2 caught that the prior `Partial<Record<…, unknown>>` made these
   keys incompatible with `React.HTMLAttributes` — wrappers like
   `interface WrapperProps extends CardProps, React.HTMLAttributes<…>`
   failed with TS2320 because the merged member types were unknown vs
   string. Matches the Spinner / Text / Label / Skeleton /
   SectionHeader pattern. */
type AllowedRest = Pick<React.HTMLAttributes<HTMLElement>, 'id' | 'title' | 'lang' | 'dir'> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);

/* aria-hidden="true" on a focusable element is a hard spec violation:
   the element stays in DOM focus order but disappears from the
   accessibility tree, creating a keyboard-trap ghost. Strip it on
   focusable paths and warn. Static cards (no interactive, no href)
   are unaffected — a truly decorative card may legitimately want
   aria-hidden. Codex adversarial review 2026-05-18 r3 + Claude
   adversarial review r4 (kept this branch only). */
const FOCUSABLE_HOSTILE_ARIA = new Set<string>(['aria-hidden']);

/* aria-disabled is the WAI-ARIA APG-endorsed pattern for a custom
   role=button that is temporarily unavailable but should remain
   keyboard-discoverable. Per Claude adversarial review r4: we pass
   it through so adopters can implement the "discoverable disabled
   button" pattern (form submit with required fields empty; "current
   plan" tile in a pricing table; etc.). The adopter's onClick is
   responsible for honoring the state — Card emits a dev-mode
   reminder. */
const FOCUSABLE_WARN_ARIA = new Set<string>(['aria-disabled']);

/* Module-level dedupe — once-per-key per session, mirrors the
   pattern in src/kit/internal/devWarn.ts. Without this, an adopter
   re-rendering an interactive Card under StrictMode (or any
   high-frequency render loop) would get console-spam on every render.
   Claude adversarial review r4 finding #2. */
const _warnedHostileAria = new Set<string>();
const _warnedDisabledAria = new Set<string>();
/* Module-level "warned once" flag for the interactive+href silent-
   swallow case. Claude adversarial review r4 finding #8. */
let _warnedInteractiveHref = false;
/* Module-level "warned once" flag for the silently-dropped `style`
   prop case. ADR-017 closes inline styles by default — `unsafe_style`
   is the documented path. A typed-bag spread (or a wrapper that
   widens via `as any`) could still attach `style` at runtime, where
   `pickAllowed` silently drops it. The warn surfaces the drop once
   per session so adopters discover the contract instead of debugging
   a "my style isn't applying" mystery. Cabinet roundtable r0 finding. */
let _warnedStyleDropped = false;

function pickAllowed(rest: Record<string, unknown>, isFocusable: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      if (isFocusable && FOCUSABLE_HOSTILE_ARIA.has(key)) {
        if (!IS_PROD && !_warnedHostileAria.has(key)) {
          _warnedHostileAria.add(key);
          devWarn(
            `[JKit.Card] ignored \`${key}\` on a focusable card. ` +
              'aria-hidden + focusable creates a focus-order vs accessibility-tree ' +
              'mismatch (keyboard users land on a target that screen readers ignore). ' +
              'Drop interactive / onClick / href, or compose a different widget. ' +
              '(One warning per attribute per session.)',
          );
        }
        continue;
      }
      if (isFocusable && FOCUSABLE_WARN_ARIA.has(key)) {
        /* Card intercepts activation when aria-disabled is set
           (consumer feedback #9 + Codex PR #145 round-1
           CARD-DISABLED-4). Behavior differs by render variant:
             div/button (interactive + onClick) — onClick + keyboard
               Enter/Space both suppressed; no activation fires.
             link (href) — pointer click + Enter both call
               preventDefault so navigation is blocked. (Anchors
               don't respond to Space natively, so nothing extra
               needed for that key.)
           Both paths keep the element focusable per the WAI-ARIA
           APG "discoverable disabled" pattern; SRs still announce
           the disabled state. */
        if (!IS_PROD && !_warnedDisabledAria.has(key)) {
          _warnedDisabledAria.add(key);
          devWarn(
            `[JKit.Card] aria-disabled='true' detected. ` +
              'Card intercepts activation: for interactive (div/button) ' +
              'cards, onClick + Enter/Space are suppressed; for link ' +
              '(href) cards, pointer click, middle-click, and Enter all ' +
              'call preventDefault so navigation is blocked in every ' +
              'input mode. The element remains focusable per WAI-ARIA APG ' +
              '"discoverable disabled." CSS suppresses the hover lift and ' +
              'shows not-allowed. ' +
              '(One warning per attribute per session.)',
          );
        }
        out[key] = rest[key];
        continue;
      }
      out[key] = rest[key];
    }
  }
  return out;
}

/* Vocabulary mirrors ADR-028's ambient cascade verbatim. The four-
   specialist Cabinet roundtable (UX Architect, Software Architect,
   Frontend Developer, UX Researcher; 4/4 convergence, 2026-05-20)
   rejected a `size`-axis on Card in favor of this shape — `size`
   collides with Button's lexicon, and adopters already learn the
   density triple once for the provider. PatternFly ships the
   identical triple (`compact | standard | large`) on its Card
   primitive (UX Researcher survey: 7/12 systems ship tile-density
   on Card; PatternFly cited as the closest API analog). */
export type CardDensity = 'compact' | 'default' | 'comfortable';
export type CardVariant = 'default' | 'nested';

export interface CardProps extends StyledProps, WithChildren, UnsafeProps, AllowedRest {
  /* When true AND onClick is set, the card becomes a keyboard-
     operable activation target: role="button", tabIndex=0, and
     Enter / Space fire onClick the same way a pointer click would.
     The 3.0.0 behaviour was pointer-only; the kit now ships the
     affordance by default because rendering an `interactive`-looking
     card without keyboard support is an a11y bug in waiting. */
  interactive?: boolean;
  onClick?: CardActivationHandler | MouseEventHandler<HTMLElement>;
  /* Render the card as an anchor tag for navigation links. When set,
     the card renders as <a href={href}> instead of <div>. This gives
     proper link semantics (right-click → open in new tab, cmd-click,
     etc.) while keeping the card's visual treatment. */
  href?: string;
  target?: string;
  rel?: string;
  /* Per-instance density override (ADR-028 v5.1 amendment).
     When set, emits `data-density={density}` on the rendered root.
     The direct-selector form in density.css
     (`.j-card[data-density='…']`) takes precedence over the ambient
     `:where(:root)[data-density='…'] .j-card` cascade — a Card
     inside `<JacquardProvider density="comfortable">` that passes
     `density="compact"` paints compact.

     Use this for one-off tile grids (2-column pickers, asset
     thumbnail rails) where the surrounding surface should stay at
     ambient density. For a whole sidebar/section, prefer
     `<JacquardProvider density>` so every primitive (Card, Modal,
     Toast, Empty, Table, Form, Field) flexes together — see
     ADR-028 §"Cascade contract" for the per-instance vs ambient
     rule of thumb.

     When omitted (default), the attribute is NOT emitted and the
     ambient cascade owns the resolved density. Density does NOT
     compound across nesting (ADR-028 §"Cascade contract"). */
  density?: CardDensity;
  /* Geometry variant for cards nested inside another raised surface.
     `nested` reduces the corner radius so inner cards read
     concentrically without adopter descendant selectors. */
  variant?: CardVariant;
}

export const Card = React.forwardRef<HTMLElement, CardProps>(function Card(
  {
    children,
    interactive,
    onClick,
    className,
    href,
    target,
    rel,
    density,
    variant = 'default',
    unsafe_className,
    unsafe_style,
    ...rest
  },
  ref,
) {
  const isLink = Boolean(href);
  const isActivationTarget = Boolean(interactive && onClick) && !isLink;

  /* aria-disabled handling (consumer feedback #9, 2026-05-20):
     WAI-ARIA APG "discoverable disabled" — the element keeps focus
     but loses activation. Kit now intercepts both keyboard AND
     pointer activation, so adopters set aria-disabled and the card
     stops responding without needing per-call-site onClick guards.
     CSS suppresses the hover lift + shows not-allowed. */
  const rawAriaDisabled = (rest as { 'aria-disabled'?: unknown })['aria-disabled'];
  const ariaDisabled = rawAriaDisabled === true || rawAriaDisabled === 'true';

  /* interactive + href is a silent-swallow footgun: link semantics win,
     so `interactive` is ignored (no role=button, no onKeyDown Space
     activation, no scale-tapped feedback). Adopters who pass both
     typically expect Space activation. Warn so the contradiction is
     visible. Claude adversarial review r4 finding #8. */
  if (!IS_PROD && interactive && isLink && !_warnedInteractiveHref) {
    _warnedInteractiveHref = true;
    devWarn(
      '[JKit.Card] `interactive` was passed alongside `href`. ' +
        'Link semantics win — the rendered <a> is keyboard-activated by ' +
        'Enter only (anchors do not respond to Space), and `onClick` ' +
        'fires from a pointer / Enter activation, not from Space. ' +
        'Drop `interactive` when using `href`. (One warning per session.)',
    );
  }

  /* ADR-017 closed-by-default customization model: Card's typed prop
     surface does not accept `style`. Adopters who layer inline CSS
     should reach for `unsafe_style` (the documented escape hatch). A
     typed-bag spread or `as any` widening could still smuggle `style`
     at runtime, where `pickAllowed` drops it silently — adopters then
     debug "why isn't my style applying?" instead of discovering the
     contract. Surface the drop with a one-time dev warn. Cabinet
     roundtable r0 finding (independently flagged by all three Card
     density specialists, 2026-05-20). */
  if (!IS_PROD && !_warnedStyleDropped && Object.prototype.hasOwnProperty.call(rest, 'style')) {
    _warnedStyleDropped = true;
    devWarn("[JKit.Card] dropped 'style' prop — use 'unsafe_style' (see ADR-017).");
  }

  const handleKey =
    isActivationTarget && !ariaDisabled
      ? (e: KeyboardEvent<HTMLElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            (onClick as CardActivationHandler | undefined)?.(e);
          }
        }
      : undefined;

  const classes = cx(
    'j-card',
    variant !== 'default' && `j-card-${variant}`,
    (interactive || isLink) && 'j-card-interactive',
    className,
    unsafe_className,
  );
  const mergedStyle = unsafe_style;
  const rawHandler = onClick as MouseEventHandler<HTMLElement> | undefined;
  /* aria-disabled blocks activation (paired with the keyboard
     suppression above). Two flavors:
       - div/button path: setting handler = undefined removes the
         React onClick entirely. No click → no activation.
       - link path: a bare undefined doesn't help — the anchor still
         navigates via the native href. Substitute a preventDefault
         handler so both pointer click AND Enter (which dispatches a
         synthetic click on the focused anchor before navigation)
         get intercepted. Codex PR #145 round-1 CARD-DISABLED-4. */
  const disabledLinkHandler: MouseEventHandler<HTMLElement> | undefined =
    ariaDisabled && isLink
      ? (e) => {
          e.preventDefault();
        }
      : undefined;
  const handler: MouseEventHandler<HTMLElement> | undefined = ariaDisabled
    ? disabledLinkHandler
    : rawHandler;
  /* Middle-click on an anchor fires `auxclick`, not `click`, and
     bypasses the onClick handler entirely. Without the same
     preventDefault on auxclick, a disabled link card would still
     open in a new tab when middle-clicked. WAI-ARIA APG
     "discoverable disabled" requires the control to NOT activate
     for any input method. Codex PR #145 round-2 NEW-ISSUE-D. */
  const linkRel = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);

  /* Runtime-filter rest down to the allow-list keys; the AllowedRest
     type is compile-time only and a spread bag can inject anything
     (codex Card round 1). On focusable paths (interactive activation
     target OR link), also drop stateful ARIA that would conflict with
     focusability — codex round 3. After filtering, widen for the
     spread — the contents are guaranteed to be id, title, lang, dir,
     data- prefixed and (safe) aria- prefixed only. */
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
    isActivationTarget || isLink,
  ) as React.HTMLAttributes<HTMLElement>;

  /* Per-instance density (ADR-028 v5.1). When the prop is set, emit
     `data-density={density}` on the rendered root so the direct-
     selector rule in density.css wins over the ambient cascade.
     When omitted, leave the attribute off — adopters get the ambient
     cascade for free, and a "default" Card inside a comfortable
     provider stays comfortable. Spread the attribute via an object
     literal so React strips it cleanly when undefined (vs an
     empty-string attribute that would still match in CSS). */
  const densityAttr = density ? { 'data-density': density } : undefined;

  if (isLink) {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...(restProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        {...densityAttr}
        className={classes}
        href={href}
        onClick={handler}
        onAuxClick={disabledLinkHandler}
        style={mergedStyle}
        target={target}
        rel={linkRel}
      >
        {children}
      </a>
    );
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      {...restProps}
      {...densityAttr}
      className={classes}
      onClick={handler}
      onKeyDown={handleKey}
      role={isActivationTarget ? 'button' : undefined}
      tabIndex={isActivationTarget ? 0 : undefined}
      style={mergedStyle}
    >
      {children}
    </div>
  );
});
